import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/primitives';
import { useEscape } from '@/hooks/useEscape';
import { useMountTransition } from '@/hooks/useMountTransition';
import { useOutsideClick } from '@/hooks/useOutsideClick';

export interface MenuItem {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onSelect: () => void;
  readonly tone?: 'danger';
}

/**
 * A button that opens a short list of actions — for the ones a screen needs
 * less often than the buttons beside it. Arrow keys move between the items,
 * Escape and a click elsewhere close it.
 */
export function MenuButton({
  label,
  icon,
  items,
  size = 'md',
  variant = 'default',
  align = 'end',
}: {
  readonly label: string;
  readonly icon?: LucideIcon;
  readonly variant?: 'default' | 'primary';
  /** Empty: the button is not drawn. */
  readonly items: readonly MenuItem[];
  readonly size?: 'sm' | 'md';
  readonly align?: 'start' | 'end';
}): ReactNode {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const { mounted, phase } = useMountTransition(open, 140);
  useEscape(open, () => setOpen(false));
  useOutsideClick(open, wrapper, () => setOpen(false));

  useEffect(() => {
    if (open) list.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open, mounted]);

  if (items.length === 0) return null;

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    buttons[(at + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    <div className="menu-wrap" ref={wrapper}>
      <Button
        size={size}
        variant={variant}
        {...(icon ? { icon } : {})}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
        <ChevronDown size={14} aria-hidden="true" className={open ? 'select-caret-open' : 'select-caret'} />
      </Button>
      {mounted ? (
        <div ref={list} id={menuId} className={`menu menu-${align} popover-${phase}`} role="menu" aria-label={label} onKeyDown={onKeyDown}>
          {items.map(({ label: text, icon: Icon, onSelect, tone }) => (
            <button
              key={text}
              type="button"
              role="menuitem"
              className={`menu-item${tone === 'danger' ? ' menu-item-danger' : ''}`}
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
            >
              <Icon size={15} aria-hidden="true" />
              {text}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
